import * as React from 'react';
//@ts-ignore
import { BrowserProtocol, queryMiddleware } from 'farce';
import {
  createFarceRouter,
  createRender,
  makeRouteConfig,
  Route,
  RouteRenderArgs
} from 'found';
//@ts-ignore
import { Resolver } from 'found-relay';
import environment, { isLoggedInManager } from './api/environment';
import moment from 'moment';
import { graphql } from 'react-relay';
import LoginPage from 'views/Login';
import RecoverPassword from 'views/RecoverPassword';
import { ThemeProvider } from 'react-jss';
import theme from './helpers/theme';
import AppHolder from 'views/AppHolder';
import OrgOverview from 'views/OrgOverview';
import DelegatesPage, {
  ALL_COMPLETED,
  EXPIRING_BEFORE_3,
  EXPIRING_BEFORE_6,
  HAS_ACTIVE_BACKGROUND_CHECK,
  HAS_ACTIVE_DOCUMENT,
  HAS_EXPIRED_BACKGROUND_CHECK,
  HAS_EXPIRED_DOCUMENT,
  HAS_INCOMPLETE_COURSE
} from 'views/DelegatesPage';
import CoursesPage from 'views/CoursesPage';
import DelegateProfilePage from 'views/DelegateProfilePage';
import DelegateProfilePageFrag from 'views/DelegateProfilePage';
import ErrorBoundary from 'components/ErrorBoundarys/PageBoundary';
import { SideModalProvider } from 'views/SideModalProvider';
import ResetPassword from 'views/ResetPassword';
import { DelegateDetailsProvider } from 'views/DelegateDetailsProvider';
import { ActionMenuProvider } from 'components/ActionMenu/ActionMenuProvider';
import { ToastProvider } from 'components/Toasts/ToastProvider';
import BackgroundChecks, { DEFAULT_LIMIT } from 'views/BackgroundChecks';
import BackgroundCheckDetail from 'views/BackgroundCheckDetail';
import AuditsFrag from 'views/Audits';
import MachineReports from 'views/MachineReports';
import SecurityProgrammesFrag from 'views/SecurityProgrammes';
import { validate } from 'uuid';

const autoLogin = (Comp: React.ReactNode) => (args: RouteRenderArgs) => {
  const { pathname } = args.match.location;
  //@ts-ignore
  if ((pathname === '/' || pathname === '/login') && args?.props?.manager) {
    args.match.router.push('/app');
    return;
  }
  //@ts-ignore
  return <Comp {...args.props} />;
};

let sessionStatus: 'unknown' | 'checking' | 'loggedIn' | 'loggedOut' =
  'unknown';

const ensureLoggedIn = (args: any): boolean => {
  if (args?.match?.location?.pathname === '/login') {
    return false;
  }

  if (sessionStatus === 'loggedIn') {
    return false;
  }

  if (sessionStatus === 'unknown') {
    sessionStatus = 'checking';
    isLoggedInManager().then((loggedIn: boolean) => {
      sessionStatus = loggedIn ? 'loggedIn' : 'loggedOut';
      if (!loggedIn) {
        //@ts-ignore
        args.match.router.push('/login');
      } else {
        // Confirmed logged in — force the router to re-render this route so
        // the held page now displays.
        //@ts-ignore
        args.match.router.replace(args.match.location);
      }
    });
  }

  // 'checking' or 'loggedOut' — hold with a placeholder.
  return true;
};

const protectedRenderer = (Comp: React.ReactNode) => (
  args: RouteRenderArgs
) => {
  // Sadly found-relay has no types...
  if (ensureLoggedIn(args)) {
    return <div></div>;
  }
  if (!args.props) {
    return <div></div>;
  }
  //@ts-ignore
  return <Comp {...args.props} retry={args.retry} />;
};

const Router = createFarceRouter({
  historyProtocol: new BrowserProtocol(),
  historyMiddlewares: [queryMiddleware],
  routeConfig: makeRouteConfig(
    <Route>
      <Route
        path="/(login)?"
        Component={LoginPage}
        query={graphql`
          query App_Login_Query {
            manager {
              ...LoginPage_manager
            }
          }
        `}
        render={autoLogin(LoginPage)}
      />
      <Route path="/(password)?" Component={RecoverPassword} />
      <Route path="/reset/:token" Component={ResetPassword} />
      <Route
        path="/app"
        Component={AppHolder}
        query={graphql`
          query App_Holder_Query {
            manager {
              ...AppHolder_manager
            }
          }
        `}
        render={protectedRenderer(AppHolder)}
      >
        <Route
          path="/"
          Component={OrgOverview}
          query={graphql`
            query App_Org_Query {
              manager {
                ...OrgOverview_manager
              }
              purchasedCourses {
                ...OrgOverview_purchasedCourses
              }
            }
          `}
        />
        <Route
          path="/delegates"
          Component={DelegatesPage}
          query={graphql`
            query App_DelegatesPage_Query(
              $offset: Int
              $limit: Int
              $orderBy: OrderBy
              $filter: DelegateFilter
            ) {
              delegates(
                page: { offset: $offset, limit: $limit }
                orderBy: $orderBy
                filter: $filter
              ) {
                ...DelegatesPage_delegates
              }
              manager {
                ...DelegatesPage_manager
              }
            }
          `}
          prepareVariables={(params: any, { location }: any) => {
            const {
              offset,
              limit,
              field,
              ascending,
              filter,
              secondaryCategory,
              hasActiveCert,
              hasExpiredCert,
              simulatorCompliant
            } = location.query;
            let expiringBeforeTime = undefined;

            switch (filter) {
              case EXPIRING_BEFORE_6:
                expiringBeforeTime = moment().add(6, 'months').toISOString();
                break;
              case EXPIRING_BEFORE_3:
                expiringBeforeTime = moment().add(3, 'months').toISOString();
                break;
            }

            let simCompliant = undefined;

            if (simulatorCompliant === 'true') {
              simCompliant = true;
            }
            if (simulatorCompliant === 'false') {
              simCompliant = false;
            }

            try {
              var page = {
                offset: parseInt(offset) || 0,
                limit: parseInt(limit) || 10,
                orderBy: !!field
                  ? {
                      ascending: ascending === 'true',
                      field
                    }
                  : undefined,
                filter: {
                  expiringBefore: expiringBeforeTime,
                  allCompleted: filter === ALL_COMPLETED ? true : undefined,
                  hasIncompleteCourse:
                    filter === HAS_INCOMPLETE_COURSE ? true : undefined,
                  secondaryCategoryUUID: secondaryCategory,
                  hasActiveCert: hasActiveCert === 'true',
                  hasExpiredCert: hasExpiredCert === 'true',
                  simulatorCompliant: simCompliant,
                  hasActiveDocument:
                    filter === HAS_ACTIVE_DOCUMENT ? true : undefined,
                  hasExpiredDocument:
                    filter === HAS_EXPIRED_DOCUMENT ? true : undefined,
                  hasValidBackgroundCheck:
                    filter === HAS_ACTIVE_BACKGROUND_CHECK ? true : undefined,
                  hasExpiredBackgroundCheck:
                    filter === HAS_EXPIRED_BACKGROUND_CHECK ? true : undefined
                }
              };
              return page;
            } catch (err) {}
            return {
              offset: 0,
              limit: 10
            };
          }}
        />
        <Route
          path="/delegates/:uuid"
          Component={DelegateProfilePage}
          query={graphql`
            query App_DelegatesProfile_Query($uuid: UUID!) {
              delegate(uuid: $uuid) {
                ...DelegateProfilePage_delegate
              }
              manager {
                ...DelegateProfilePage_manager
              }
            }
          `}
          prepareVariables={(params: any, { location }: any) => {
            const { uuid } = params;
            try {
              var page = {
                uuid
              };
              return page;
            } catch (err) {}
            return {
              uuid
            };
          }}
          render={(args: any) => {
            if (ensureLoggedIn(args)) {
              return <div></div>;
            }
            if (!args.props) {
              return <div></div>;
            }

            return (
              <ErrorBoundary>
                <DelegateProfilePageFrag
                  {...args.props}
                  retry={args.retry}
                  activity={args.props?.delegate.activity}
                  delegate={args.props?.delegate}
                />
              </ErrorBoundary>
            );
          }}
        />
        <Route
          path="/courses"
          Component={CoursesPage}
          query={graphql`
            query App_Courses_Query($offset: Int, $limit: Int) {
              purchasedCourses(page: { offset: $offset, limit: $limit }) {
                ...CoursesPage_courses
              }
            }
          `}
          prepareVariables={(params: any, { location }: any) => {
            const { offset, limit } = location.query;
            try {
              var page = {
                offset: parseInt(offset) || 0,
                limit: parseInt(limit) || 10
              };
              return page;
            } catch (err) {}
            return {
              offset: 0,
              limit: 10
            };
          }}
          render={(args: any) => {
            if (ensureLoggedIn(args)) {
              return <div></div>;
            }
            if (args.error && args.error != null) {
              console.error(args.error);
            }
            if (!args.props) {
              return <div></div>;
            }

            return (
              <ErrorBoundary>
                <CoursesPage
                  {...args.props}
                  router={args.match.router}
                  courses={args.props.purchasedCourses}
                />
              </ErrorBoundary>
            );
          }}
        />
        <Route
          path="/audits"
          query={graphql`
            query App_Audits_Query($filter: AuditsFilter, $page: Page) {
              audits(filter: $filter, page: $page) {
                ...Audits_audits
              }
              auditTemplates {
                ...Audits_auditTemplates
              }
            }
          `}
          Component={AuditsFrag}
          prepareVariables={(params: any, { location }: any) => {
            const { offset, limit } = location.query;

            try {
              const params = {
                page: {
                  offset: parseInt(offset) || 0,
                  limit: parseInt(limit) || DEFAULT_LIMIT
                },
                filter: {
                  searchText: location.query.search ?? undefined,
                  auditTemplateUUIDs:
                    location.query?.auditTemplates
                      ?.split(',')
                      .filter((id: string) => validate(id)) ?? undefined
                }
              };
              return params;
            } catch (err) {}
            return {
              page: {
                offset: 0,
                limit: DEFAULT_LIMIT
              }
            };
          }}
        />
        <Route
          path="/machine-reports"
          Component={MachineReports}
          query={graphql`
            query App_MachineReports_Query(
              $page: Page
              $filter: MachineReportFilter
            ) {
              machineReports(page: $page, filter: $filter) {
                ...MachineReports_machineReports
              }
            }
          `}
          prepareVariables={(params: any, { location }: any) => {
            const { offset, limit } = location.query;

            try {
              const params = {
                page: {
                  offset: parseInt(offset) || 0,
                  limit: parseInt(limit) || 25
                },
                filter: {
                  sent: true,
                  searchText: location.query.search || undefined
                }
              };
              return params;
            } catch (err) {}
            return {
              page: {
                offset: 0,
                limit: 25
              },
              filter: {
                sent: true
              }
            };
          }}
        />
        <Route
          path="/background-checks"
          Component={BackgroundChecks}
          query={graphql`
            query App_BackgroundChecks_Query(
              $page: Page
              $filter: BackgroundCheckFilter
            ) {
              backgroundChecks(page: $page, filter: $filter) {
                ...BackgroundChecks_backgroundChecks
              }
            }
          `}
          prepareVariables={(params: any, { location }: any) => {
            const { offset, limit } = location.query;

            try {
              const params = {
                page: {
                  offset: parseInt(offset) || 0,
                  limit: parseInt(limit) || DEFAULT_LIMIT
                },
                filter: {
                  searchText: location.query.search ?? undefined,
                  approved: location.query.approved === 'true' || undefined,
                  applicationWaiting:
                    location.query.applicationWaiting === 'true' || undefined,
                  responsesWaiting:
                    location.query.responsesWaiting === 'true' || undefined,
                  approvalWaiting:
                    location.query.approvalWaiting === 'true' || undefined
                }
              };
              return params;
            } catch (err) {}
            return {
              page: {
                offset: 0,
                limit: DEFAULT_LIMIT
              }
            };
          }}
        />
        <Route
          path="/background-checks/:uuid"
          Component={BackgroundCheckDetail}
          query={graphql`
            query App_BackgroundCheckDetail_Query($uuid: UUID!) {
              backgroundCheck(uuid: $uuid) {
                ...BackgroundCheckDetail_backgroundCheck
              }
            }
          `}
          prepareVariables={(params: any) => {
            return {
              uuid: params.uuid
            };
          }}
          render={protectedRenderer(BackgroundCheckDetail)}
        />
        <Route
          path="/security-programmes"
          Component={SecurityProgrammesFrag}
          query={graphql`
            query App_SecurityProgrammes_Query(
              $page: Page
              $filter: SecurityProgrammeFilter
            ) {
              manager {
                ...SecurityProgrammes_manager
              }
            }
          `}
          prepareVariables={(params: any, { location }: any) => {
            const { offset, limit } = location.query;

            try {
              const params = {
                page: {
                  offset: parseInt(offset) || 0,
                  limit: parseInt(limit) || DEFAULT_LIMIT
                },
                filter: {
                  name: location.query.search ?? undefined
                }
              };
              return params;
            } catch (err) {}
            return {
              page: {
                offset: 0,
                limit: DEFAULT_LIMIT
              }
            };
          }}
        />
      </Route>
    </Route>
  ),
  render: createRender({})
});

const App = () => (
  <ThemeProvider theme={theme}>
    <ToastProvider>
      <ActionMenuProvider>
        <SideModalProvider>
          <DelegateDetailsProvider>
            <Router resolver={new Resolver(environment)} />
          </DelegateDetailsProvider>
        </SideModalProvider>
      </ActionMenuProvider>
    </ToastProvider>
  </ThemeProvider>
);

export default App;
